import Vue from 'vue';
import $ from 'jquery';
import { mapGetters } from 'vuex';
import AbstractSortableGrid from './AbstractSortableGrid.vue';
import Project from 'src/api/project';
import { LOAD_PROJECT } from 'src/store/types';
import Modal from 'components/widgets/Modal.vue';
import ModalEditText from 'components/widgets/ModalEditText.vue';
import DataUtils from 'src/utils/data';
import { debounce } from 'lodash';
import { BfButton } from 'components/Butterfly';
export default Vue.extend({
    components: { AbstractSortableGrid, Modal, ModalEditText, BfButton },
    props: {},
    data() {
        return {
            pageNumber: 1,
            pageSize: 4,
            sortBy: '',
            gettingAnalyses: false,
            analysisToDelete: null,
            analysisToUpdate: null,
            data: {},
            sortOptions: [
                { key: '-modified', label: 'Date Modified' },
                { key: '-created', label: 'Date Created' },
                { key: 'name', label: 'Alphanumeric' }
            ],
            getAnalysesDebounced: debounce((pageNumber, sortBy) => {
                if (!this.projectId) {
                    // Route might have navigated away by the time the debounce
                    // fires. In that case projectId will not be defined so don't
                    // bother getting Analyses for it.
                    // TODO: see the TODO in the projectId computed prop
                    return;
                }
                return Project.getAnalyses(pageNumber, this.projectId, sortBy)
                    .then((response) => {
                    if (this.pageNumber === pageNumber) {
                        this.data = response;
                    }
                }).catch(({ status }) => {
                    // We aren't concerned about 401s here because it is either:
                    // 1. The user does not have permission to view the analyses
                    // 2. The user's token has expired.
                    // In scenario 1 - this is handled at the project container level
                    // In scenario 2 - this is handled with a prompt for reauth
                    if (status !== 401) {
                        throw new Error(`Unhandled API response for Project.getAnalyses, status: ${status}`);
                    }
                }).finally(() => {
                    this.gettingAnalyses = false;
                });
            }, 500),
            viewAnalysisAlreadyClicked: false,
        };
    },
    computed: {
        ...mapGetters([
            'currentProject',
            'featureFlags',
            'currentUser',
        ]),
        analyses() {
            return this.data.results;
        },
        hasAnalyses() {
            return this.analyses && this.analyses.length > 0;
        },
        isDataProcessing() {
            return !!this.currentProject?.processing;
        },
        projectId() {
            // TODO: This is messy. rather receive the projectId via prop
            return this.$route.params.projectId;
        },
    },
    watch: {
        isDataProcessing(current, prev) {
            if (prev === true && current === false) {
                this.getAnalyses(this.pageNumber, this.sortBy, true, false);
            }
        },
    },
    mounted() {
        this.sortBy = this.sortOptions[0].key;
        this.getAnalyses(1, this.sortBy, true);
        // Init analysis actions dropdowns
        this.$nextTick(() => {
            $(this.$el).find('.actions .dropdown').dropdown({
                action: 'hide',
                transition: 'drop'
            });
        });
    },
    methods: {
        // Returns true if the analysis doesn't have a concept model stored on the index,
        // AND there is a feature flag allowing the upgrade.
        canGenerateMissingConceptModel(analysis) {
            return analysis.missing_concept_model && this.featureFlags && this.featureFlags.upgrade_analysis_concept_model;
        },
        async deleteAnalysis() {
            try {
                await Project.deleteAnalysis(this.projectId, this.analysisToDelete.id);
            }
            catch (e) {
                console.warn('Error while deleting analysis:', e);
            }
            finally {
                this.analysisToDelete = null;
                this.getAnalyses(this.pageNumber, this.sortBy, true);
            }
        },
        async cloneAnalysis(analysis) {
            this.gettingAnalyses = true;
            try {
                await Project.cloneAnalysis(this.projectId, analysis.id);
                await this.getAnalyses(this.pageNumber, this.sortBy, true);
            }
            finally {
                if (this.gettingAnalyses) {
                    this.gettingAnalyses = false;
                }
            }
        },
        async toggleAnalysisStale(analysis) {
            this.gettingAnalyses = true;
            try {
                await Project.updateAnalysisDisplayAttributes(this.$store, {
                    stale: analysis.stale ? 0 : 1,
                }, analysis);
                await this.getAnalyses(this.pageNumber, this.sortBy, true);
            }
            finally {
                if (this.gettingAnalyses) {
                    this.gettingAnalyses = false;
                }
            }
        },
        async rerunAnalysis(analysis) {
            this.gettingAnalyses = true;
            try {
                await Project.rerunAnalysis(analysis.project, analysis.id, {});
                await this.getAnalyses(this.pageNumber, this.sortBy, true);
            }
            finally {
                if (this.gettingAnalyses) {
                    this.gettingAnalyses = false;
                }
                this.$analytics.track.analysis.update(analysis.id);
            }
        },
        displayError(analysis) {
            if (analysis.hasOwnProperty('status_text') && analysis.status_text.toLowerCase().includes('too little data')) {
                return 'Insufficient Data';
            }
            else if (analysis.hasOwnProperty('status_text') && analysis.status_text.toLowerCase().includes('no words')) {
                return 'No Data Matches Analysis Filters';
            }
            return 'Unexpected Problem';
        },
        errorDetail(analysis) {
            if (analysis.hasOwnProperty('status_text')
                && analysis.status_text.toLowerCase().includes('too little data to identify enough concepts')) {
                return analysis.status_text;
            }
        },
        hasLowRecordCount(analysis) {
            return analysis.status === 'Finished' &&
                analysis.hasOwnProperty('data_units') &&
                analysis.data_units < 400;
        },
        formatDate: DataUtils.formatDate,
        getAnalyses(pageNumber, sortBy, force = false, background = false) {
            if (force || (pageNumber !== this.pageNumber)) {
                this.gettingAnalyses = !background;
                this.pageNumber = pageNumber;
                // Refresh project
                this.$store.dispatch({
                    type: LOAD_PROJECT,
                    projectId: this.projectId
                });
                return this.getAnalysesDebounced(pageNumber, sortBy);
            }
        },
        getDates(analysis) {
            return DataUtils.parseAnalysisDates(analysis);
        },
        // Traverse project schema and check for date fields
        hasDate() {
            for (let val of this.currentProject?.schema) {
                if (val.type === Project.COLUMN_LABELED_TYPES.get('DATE') || val.type === Project.COLUMN_LABELED_TYPES.get('DATETIME')) {
                    return true;
                }
            }
            return false;
        },
        isAnalysisViewable(id) {
            let analysis = this.data.results.find(a => a.id === id);
            return (!this.viewAnalysisAlreadyClicked
                && !analysis.old_topic_model
                && analysis.status.toUpperCase() === 'FINISHED');
        },
        navigateToCreateAnalysis() {
            this.$router.push({ name: 'analysis-create', params: { 'id': this.projectId } });
        },
        // Handle mouse leaving a row -- close its dropdown
        onMouseLeave(event) {
            let rowEl = event.target;
            $(rowEl).find('.dropdown').dropdown('hide');
        },
        openDropdown(event) {
            $(event.target.parentNode).dropdown('show');
        },
        showDeleteAnalysisModal(analysis) {
            this.analysisToDelete = analysis;
            this.$refs.confirmDeleteModal.show();
        },
        showUpdateAnalysisModal(analysis) {
            this.analysisToUpdate = analysis;
            this.$refs.confirmUpdateModal.show();
        },
        // Upgrade analysis with concept model
        async upgradeAnalysis(analysis) {
            this.analysisToUpgrade = analysis;
            try {
                // We actually just hijack the update analysis endpoint, because the concept
                // model is regenerated when updating statistics.
                await Project.rerunAnalysis(this.projectId, this.analysisToUpgrade.id, {});
                this.$analytics.track.analysis.upgrade(this.analysisToUpgrade.id);
                this.getAnalyses(this.pageNumber, this.sortBy, true);
            }
            finally {
                this.analysisToUpgrade = null;
            }
        },
        viewAnalysis(id) {
            if (this.viewAnalysisAlreadyClicked) {
                // It is possible that the user can click the analysis row twice
                // rapidly, like a double-click. This can happen fast enough that
                // that this click event handler gets called multiple times before
                // the $router.push actually changes the page. So we set a flag
                // to know if the analysis has already been clicked.
                return;
            }
            // Look up the specific analysis by id. If that analysis is not
            // viewable, do nothing.
            let analysis = this.data.results.find(a => a.id === id);
            if (!this.isAnalysisViewable(id)) {
                return;
            }
            this.$router.push({
                name: 'view-analysis',
                params: { projectId: analysis.project, analysisId: analysis.id }
            }, () => undefined, () => this.viewAnalysisAlreadyClicked = false);
            this.viewAnalysisAlreadyClicked = true;
        },
        showEditAnalysisNameModal(analysis) {
            this.activeAnalysis = analysis;
            this.proposedAnalysisName = this.activeAnalysis.name;
            this.$refs.AnalysisEditName.show(analysis.name, analysis);
        },
        async changeAnalysisName(newName, analysis) {
            await Project.modifyAnalysis(analysis, { name: newName });
            await this.getAnalyses(this.pageNumber, this.sortBy, true);
        },
        analysisSubmenuVisible(analysis) {
            // Analysis submenu is only visible if the analysis is not currently
            // being run, updated, or otherwise busy - except is a user is staff
            // in which case the submenu is always visible. This allows staff
            // to change analysis status and/or rerun the analysis.
            const busyStates = ['PROCESSING', 'UPDATING'];
            let busy = busyStates.includes(analysis.status.toUpperCase());
            let is_staff = this.currentUser && this.currentUser.is_staff;
            return is_staff || !busy;
        },
    }
});
